/**
 * BookingDetailContent — Custom detail view for Booking documents.
 *
 * Hotel front-desk style layout:
 *   Left column: Booking info card, check-in timeline card, notes
 *   Right sidebar: Customer card, linked documents
 *
 * Replaces the generic ERPNext form for Booking doctype.
 */

import { useState, useCallback } from "react";
import {
  CalendarDays, Clock, User, Phone, Mail, LogIn, LogOut,
  CheckCircle2, Package, FileText, CreditCard, StickyNote,
  Loader2, ExternalLink,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/i18n/useLanguage";
import { getERPClient } from "@/lib/erp/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Link } from "react-router-dom";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BookingDetailContentProps {
  formData: Record<string, any>;
  onRefresh?: () => void;
}

// ---------------------------------------------------------------------------
// Status badge (shared with BookingCardList)
// ---------------------------------------------------------------------------

const STATUS_STYLES: Record<string, { bg: string; text: string; dot: string }> = {
  Pending:   { bg: "bg-amber-50 dark:bg-amber-900/20", text: "text-amber-700 dark:text-amber-400", dot: "bg-amber-500" },
  Confirmed: { bg: "bg-blue-50 dark:bg-blue-900/20", text: "text-blue-700 dark:text-blue-400", dot: "bg-blue-500" },
  Paid:      { bg: "bg-emerald-50 dark:bg-emerald-900/20", text: "text-emerald-700 dark:text-emerald-400", dot: "bg-emerald-500" },
  Completed: { bg: "bg-neutral-100 dark:bg-neutral-800/40", text: "text-neutral-600 dark:text-neutral-400", dot: "bg-neutral-400" },
  Cancelled: { bg: "bg-red-50 dark:bg-red-900/20", text: "text-red-600 dark:text-red-400", dot: "bg-red-500" },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

function formatTime(timeStr: string | undefined): string {
  if (!timeStr) return "—";
  const [h, m] = timeStr.split(":");
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${h12}:${m} ${ampm}`;
}

function formatDatetime(dt: string | undefined): string {
  if (!dt) return "—";
  const d = new Date(dt);
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
}

function formatCurrency(amount: number | undefined): string {
  if (amount === undefined || amount === null) return "—";
  return `฿${Number(amount).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

// ---------------------------------------------------------------------------
// Card shell
// ---------------------------------------------------------------------------

function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950", className)}>
      {children}
    </div>
  );
}

function CardHeader({ children, icon }: { children: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 px-5 py-3.5 border-b border-neutral-100 dark:border-neutral-800/60">
      {icon && <span className="text-neutral-400 dark:text-neutral-500">{icon}</span>}
      <h3 className="text-[13px] font-semibold text-neutral-900 dark:text-neutral-100">{children}</h3>
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between py-2.5 px-5">
      <span className="text-[12px] text-neutral-500 dark:text-neutral-400 shrink-0">{label}</span>
      <span className="text-[13px] font-medium text-neutral-900 dark:text-neutral-100 text-right">{children}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function BookingDetailContent({ formData, onRefresh }: BookingDetailContentProps) {
  const { language } = useLanguage();
  const th = language === "th";

  const status = String(formData.status ?? "Pending");
  const statusStyle = STATUS_STYLES[status] ?? STATUS_STYLES.Pending;
  const checkedInAt = formData.checked_in_at as string | undefined;
  const checkedOutAt = formData.checked_out_at as string | undefined;
  const isCheckedIn = !!checkedInAt;
  const isCheckedOut = !!checkedOutAt;
  const isCancelled = status === "Cancelled";
  const isCompleted = status === "Completed";
  const canCheckIn = !isCheckedIn && !isCancelled && !isCompleted;
  const canCheckOut = isCheckedIn && !isCheckedOut && !isCancelled;
  const bookingId = String(formData.name ?? "");

  // Action state
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ type: "in" | "out" } | null>(null);

  const executeAction = useCallback(async () => {
    if (!confirmDialog) return;
    const { type } = confirmDialog;
    setConfirmDialog(null);
    setLoadingAction(type);
    try {
      const client = getERPClient();
      const method = type === "in"
        ? "zaviago_backend.storefront.booking_admin.check_in_booking"
        : "zaviago_backend.storefront.booking_admin.check_out_booking";
      await client.call(method, { booking_id: bookingId });
      toast.success(type === "in" ? (th ? "เช็คอินสำเร็จ" : "Checked in") : (th ? "เช็คเอาท์สำเร็จ" : "Checked out"));
      onRefresh?.();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : `Check-${type} failed`;
      toast.error(msg);
    } finally {
      setLoadingAction(null);
    }
  }, [confirmDialog, bookingId, th, onRefresh]);

  return (
    <div className="md:flex md:gap-5 px-4 pb-8">
      {/* ── Left column ── */}
      <div className="md:flex-1 md:min-w-0 space-y-4">

        {/* Booking Info */}
        <Card>
          <CardHeader icon={<CalendarDays className="w-4 h-4" />}>
            {th ? "ข้อมูลการจอง" : "Booking Details"}
          </CardHeader>
          <div className="divide-y divide-neutral-100 dark:divide-neutral-800/60">
            <FieldRow label={th ? "สถานะ" : "Status"}>
              <span className={cn("inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium", statusStyle.bg, statusStyle.text)}>
                <span className={cn("w-1.5 h-1.5 rounded-full", statusStyle.dot)} />
                {status}
              </span>
            </FieldRow>
            <FieldRow label={th ? "รายการ" : "Item"}>
              {formData.item_name || formData.item_code || "—"}
            </FieldRow>
            <FieldRow label={th ? "วันที่" : "Date"}>
              {formatDate(formData.booking_date)}
            </FieldRow>
            <FieldRow label={th ? "เวลา" : "Time"}>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-neutral-400" />
                {formatTime(formData.start_time)} – {formatTime(formData.end_time)}
              </span>
            </FieldRow>
            <FieldRow label={th ? "ราคา" : "Price"}>
              <span className="tabular-nums">{formatCurrency(formData.price)}</span>
            </FieldRow>
          </div>
        </Card>

        {/* Check-in / Check-out */}
        {!isCancelled && (
          <Card>
            <CardHeader icon={<LogIn className="w-4 h-4" />}>
              {th ? "เช็คอิน / เช็คเอาท์" : "Check-in / Check-out"}
            </CardHeader>
            <div className="px-5 py-4">
              {/* Timeline */}
              <div className="space-y-4">
                {/* Check-in step */}
                <div className="flex items-start gap-3">
                  <div className={cn(
                    "mt-0.5 flex items-center justify-center w-7 h-7 rounded-full shrink-0",
                    isCheckedIn
                      ? "bg-emerald-100 dark:bg-emerald-900/30"
                      : "bg-neutral-100 dark:bg-neutral-800",
                  )}>
                    <LogIn className={cn("w-3.5 h-3.5", isCheckedIn ? "text-emerald-600 dark:text-emerald-400" : "text-neutral-400")} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-neutral-900 dark:text-neutral-100">
                      {th ? "เช็คอิน" : "Check In"}
                    </div>
                    {isCheckedIn ? (
                      <div className="text-[12px] text-emerald-600 dark:text-emerald-400 mt-0.5">
                        {formatDatetime(checkedInAt)}
                      </div>
                    ) : (
                      <div className="text-[12px] text-neutral-400 mt-0.5">
                        {th ? "ยังไม่ได้เช็คอิน" : "Not yet"}
                      </div>
                    )}
                  </div>
                  {canCheckIn && (
                    <Button
                      size="sm"
                      className="h-8 px-3 text-[12px]"
                      disabled={!!loadingAction}
                      onClick={() => setConfirmDialog({ type: "in" })}
                    >
                      {loadingAction === "in" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (th ? "เช็คอิน" : "Check In")}
                    </Button>
                  )}
                </div>

                {/* Connector line */}
                <div className="ml-3.5 w-px h-4 bg-neutral-200 dark:bg-neutral-700" />

                {/* Check-out step */}
                <div className="flex items-start gap-3">
                  <div className={cn(
                    "mt-0.5 flex items-center justify-center w-7 h-7 rounded-full shrink-0",
                    isCheckedOut
                      ? "bg-neutral-200 dark:bg-neutral-700"
                      : "bg-neutral-100 dark:bg-neutral-800",
                  )}>
                    <LogOut className={cn("w-3.5 h-3.5", isCheckedOut ? "text-neutral-600 dark:text-neutral-400" : "text-neutral-400")} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-neutral-900 dark:text-neutral-100">
                      {th ? "เช็คเอาท์" : "Check Out"}
                    </div>
                    {isCheckedOut ? (
                      <div className="text-[12px] text-neutral-500 mt-0.5">
                        {formatDatetime(checkedOutAt)}
                      </div>
                    ) : (
                      <div className="text-[12px] text-neutral-400 mt-0.5">
                        {th ? "ยังไม่ได้เช็คเอาท์" : "Not yet"}
                      </div>
                    )}
                  </div>
                  {canCheckOut && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 px-3 text-[12px]"
                      disabled={!!loadingAction}
                      onClick={() => setConfirmDialog({ type: "out" })}
                    >
                      {loadingAction === "out" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (th ? "เช็คเอาท์" : "Check Out")}
                    </Button>
                  )}
                </div>
              </div>

              {/* Completed state */}
              {isCheckedIn && isCheckedOut && (
                <div className="mt-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  <span className="text-[12px] font-medium text-emerald-700 dark:text-emerald-400">
                    {th ? "เสร็จสิ้นเรียบร้อย" : "Visit completed"}
                  </span>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Notes */}
        {formData.notes && (
          <Card>
            <CardHeader icon={<StickyNote className="w-4 h-4" />}>
              {th ? "หมายเหตุ" : "Notes"}
            </CardHeader>
            <div className="px-5 py-4">
              <p className="text-[13px] text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap leading-relaxed">
                {formData.notes}
              </p>
            </div>
          </Card>
        )}
      </div>

      {/* ── Right sidebar ── */}
      <div className="w-full md:w-[320px] shrink-0 space-y-4 mt-4 md:mt-0">

        {/* Customer */}
        <Card>
          <CardHeader icon={<User className="w-4 h-4" />}>
            {th ? "ข้อมูลลูกค้า" : "Customer"}
          </CardHeader>
          <div className="px-5 py-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-9 h-9 rounded-full bg-primary/10 text-primary text-[14px] font-semibold shrink-0">
                {(formData.customer_name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                  {formData.customer_name || "—"}
                </div>
                {formData.customer && (
                  <div className="text-[11px] text-neutral-400 truncate">{formData.customer}</div>
                )}
              </div>
            </div>
            {formData.customer_phone && (
              <a href={`tel:${formData.customer_phone}`} className="flex items-center gap-2 text-[12px] text-neutral-600 dark:text-neutral-400 hover:text-primary transition-colors">
                <Phone className="w-3.5 h-3.5 text-neutral-400" />
                {formData.customer_phone}
              </a>
            )}
            {formData.customer_email && (
              <a href={`mailto:${formData.customer_email}`} className="flex items-center gap-2 text-[12px] text-neutral-600 dark:text-neutral-400 hover:text-primary transition-colors">
                <Mail className="w-3.5 h-3.5 text-neutral-400" />
                {formData.customer_email}
              </a>
            )}
            {!formData.customer_name && !formData.customer_phone && !formData.customer_email && (
              <div className="text-[12px] text-neutral-400 italic">{th ? "ไม่มีข้อมูลลูกค้า" : "No customer info"}</div>
            )}
          </div>
        </Card>

        {/* Linked documents */}
        <Card>
          <CardHeader icon={<FileText className="w-4 h-4" />}>
            {th ? "เอกสารที่เกี่ยวข้อง" : "Linked Documents"}
          </CardHeader>
          <div className="divide-y divide-neutral-100 dark:divide-neutral-800/60">
            {formData.sales_order && (
              <Link
                to={`/orders?detail=${formData.sales_order}`}
                className="flex items-center justify-between px-5 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-900/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <CreditCard className="w-3.5 h-3.5 text-neutral-400" />
                  <span className="text-[12px] text-neutral-600 dark:text-neutral-400">{th ? "ใบสั่งขาย" : "Sales Order"}</span>
                </div>
                <div className="flex items-center gap-1 text-[12px] font-medium text-primary">
                  {formData.sales_order}
                  <ExternalLink className="w-3 h-3" />
                </div>
              </Link>
            )}
            {formData.item_code && (
              <Link
                to={`/inventory/item/${formData.item_code}`}
                className="flex items-center justify-between px-5 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-900/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Package className="w-3.5 h-3.5 text-neutral-400" />
                  <span className="text-[12px] text-neutral-600 dark:text-neutral-400">{th ? "สินค้า" : "Item"}</span>
                </div>
                <div className="flex items-center gap-1 text-[12px] font-medium text-primary">
                  {formData.item_code}
                  <ExternalLink className="w-3 h-3" />
                </div>
              </Link>
            )}
            {!formData.sales_order && !formData.item_code && (
              <div className="px-5 py-3 text-[12px] text-neutral-400 italic">
                {th ? "ไม่มีเอกสารที่เกี่ยวข้อง" : "No linked documents"}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* ── Confirm dialog ── */}
      <AlertDialog open={!!confirmDialog} onOpenChange={(open) => { if (!open) setConfirmDialog(null); }}>
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDialog?.type === "in"
                ? (th ? "ยืนยันเช็คอิน" : "Confirm Check In")
                : (th ? "ยืนยันเช็คเอาท์" : "Confirm Check Out")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog?.type === "in"
                ? (th ? "ยืนยันว่าลูกค้ามาถึงแล้ว ระบบจะบันทึกเวลาเช็คอิน" : "Confirm the customer has arrived. The system will record the check-in time.")
                : (th ? "ยืนยันว่าลูกค้าออกแล้ว ระบบจะบันทึกเวลาเช็คเอาท์" : "Confirm the customer has left. The system will record the check-out time.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 p-3 my-2">
            <div className="text-[13px] font-medium text-neutral-900 dark:text-neutral-100">
              {formData.customer_name || "—"}
            </div>
            <div className="text-[12px] text-neutral-500 mt-1">
              {formData.item_name || formData.item_code} · {formatTime(formData.start_time)} – {formatTime(formData.end_time)}
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{th ? "ยกเลิก" : "Cancel"}</AlertDialogCancel>
            <AlertDialogAction
              onClick={executeAction}
              className={cn(
                "flex-1",
                confirmDialog?.type === "in"
                  ? "bg-primary hover:bg-primary/90"
                  : "bg-neutral-800 hover:bg-neutral-700 dark:bg-neutral-200 dark:hover:bg-neutral-300 dark:text-neutral-900",
              )}
            >
              {confirmDialog?.type === "in" ? (th ? "เช็คอิน" : "Check In") : (th ? "เช็คเอาท์" : "Check Out")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
